import { createFeature, createReducer, on } from '@ngrx/store';
import { Ticket } from '../../../types/interfaces/ticket';
import * as TicketActions from './actions';
import { MessageTemplate } from '../../../types/interfaces/message-templates';
import { ChatMessage } from '../../../types/interfaces/message';
import { ChatUserAttributes } from '../../../types/interfaces/chat-user';
import { logoutSuccess } from '../../../shared/store/actions';
import { findMessageIndex } from '../../../utils/message-identity';
import { MessageStatusEnum } from '../../../types/enums/message-status';

export interface State {
  tickets: Ticket[];
  historyTickets: Ticket[];
  historyHasMore: boolean;
  ticketSelected: Ticket | undefined;
  ticketSelectedMessages: ChatMessage[];
  messageTemplates: MessageTemplate[];
  chatUsers: ChatUserAttributes[];
  ticketCreated: boolean;
  ticketCreating: boolean;
  ticketCreateError: string | null;
}

export const initialState: State = {
  tickets: [],
  historyTickets: [],
  historyHasMore: false,
  ticketSelected: undefined,
  ticketSelectedMessages: [],
  messageTemplates: [],
  chatUsers: [],
  ticketCreated: false,
  ticketCreating: false,
  ticketCreateError: null,
};

function ticketTime(ticket: Ticket): number | null {
  if (!ticket.updatedAt) {
    return null;
  }

  const timestamp = new Date(ticket.updatedAt).getTime();
  return Number.isNaN(timestamp) ? null : timestamp;
}

function isOlderTicket(existing: Ticket, incoming: Ticket): boolean {
  const existingTime = ticketTime(existing);
  const incomingTime = ticketTime(incoming);

  return (
    existingTime !== null &&
    incomingTime !== null &&
    incomingTime < existingTime
  );
}

function upsertTicket(
  tickets: Ticket[],
  ticket: Ticket,
  insertIfMissing: boolean,
  markUnread = false,
): Ticket[] {
  const index = tickets.findIndex((item) => item.id == ticket.id);
  const nextTicket = markUnread ? { ...ticket, unread: true } : ticket;

  if (index === -1) {
    return insertIfMissing ? [nextTicket, ...tickets] : tickets;
  }

  if (isOlderTicket(tickets[index], ticket)) {
    return tickets;
  }

  return [
    ...tickets.slice(0, index),
    nextTicket,
    ...tickets.slice(index + 1),
  ];
}

function restoreFailedAgentMessage(
  ticket: Ticket,
  tempId: string,
  previousLastMessage?: ChatMessage,
): Ticket {
  return ticket.lastMessage?.tempId === tempId
    ? { ...ticket, lastMessage: previousLastMessage }
    : ticket;
}

function isStaleMessageStatus(current: number, incoming: number): boolean {
  const isBeforeDelivery =
    incoming === MessageStatusEnum.UPLOADING ||
    incoming === MessageStatusEnum.PENDING ||
    incoming === MessageStatusEnum.SENT ||
    incoming === MessageStatusEnum.UPLOAD_FAILED;

  if (current === MessageStatusEnum.READ) {
    return isBeforeDelivery || incoming === MessageStatusEnum.DELIVERED ||
      incoming === MessageStatusEnum.FAILED;
  }
  if (current === MessageStatusEnum.DELIVERED) {
    return isBeforeDelivery || incoming === MessageStatusEnum.FAILED;
  }
  return current === MessageStatusEnum.FAILED && isBeforeDelivery;
}

export const ticketsFeature = createFeature({
  name: 'tickets',
  reducer: createReducer(
    initialState,
    on(TicketActions.initialTickets, (state, action) => {
      return {
        ...state,
        tickets: action.tickets,
      };
    }),
    on(TicketActions.newTicket, (state, action) => {
      return {
        ...state,
        tickets: upsertTicket(state.tickets, action.ticket, true, true),
      };
    }),
    on(TicketActions.updateTicket, (state, action) => {
      const isTicketSelected =
        state.ticketSelected && state.ticketSelected.id === action.ticket.id;
      const existingTicket = state.tickets.find(
        (ticket) => ticket.id == action.ticket.id,
      );
      const nextTickets = upsertTicket(state.tickets, action.ticket, true);
      const applied =
        !existingTicket ||
        nextTickets.find((ticket) => ticket.id == action.ticket.id) ===
          action.ticket;

      return {
        ...state,
        tickets: nextTickets,
        historyTickets: upsertTicket(
          state.historyTickets,
          action.ticket,
          false,
        ),
        ticketSelected:
          isTicketSelected && applied ? action.ticket : state.ticketSelected,
      };
    }),
    on(TicketActions.removeTicket, (state, action) => {
      return {
        ...state,
        tickets: state.tickets.filter((t) => t.id != action.ticketId),
        ticketSelected:
          action.ticketId == state.ticketSelected?.id
            ? undefined
            : state.ticketSelected,
        ticketSelectedMessages:
          action.ticketId == state.ticketSelected?.id
            ? []
            : state.ticketSelectedMessages,
      };
    }),
    on(TicketActions.ticketAssigned, (state, action) => {
      return {
        ...state,
        tickets: upsertTicket(state.tickets, action.ticket, true),
        historyTickets: upsertTicket(
          state.historyTickets,
          action.ticket,
          false,
        ),
      };
    }),
    on(TicketActions.selectTicket, (state, action) => {
      if (!action.ticket) {
        return {
          ...state,
          ticketSelected: undefined,
          ticketSelectedMessages: [],
        };
      }

      const deselect = action.ticket.id === state.ticketSelected?.id;

      return {
        ...state,
        ticketSelected: deselect ? undefined : action.ticket,
        ticketSelectedMessages: [], // populate later when messages are loaded
      };
    }),
    on(TicketActions.newMessage, (state, action) => {
      const ticket = state.tickets.find((x) => x.id == action.idTicket);

      if (!ticket) {
        return {
          ...state,
        };
      }

      const isTicketSelected =
        state.ticketSelected && ticket.id == state.ticketSelected.id;

      const newTicketData: Ticket = {
        ...ticket,
        lastMessage: action.message,
      };

      return {
        ...state,
        tickets: [
          newTicketData,
          ...state.tickets.filter((x) => x.id != newTicketData.id),
        ],
        ticketSelected: isTicketSelected ? newTicketData : state.ticketSelected,
        ticketSelectedMessages: isTicketSelected
          ? [...state.ticketSelectedMessages, action.message]
          : state.ticketSelectedMessages,
      };
    }),
    on(TicketActions.updateMessage, (state, action) => {
      if (!state.ticketSelected) {
        return { ...state };
      }

      const messageChatId = action.message.idChat;

      const isFromSelectedTicket =
        state.ticketSelected && state.ticketSelected.idChat == messageChatId;

      const messageIndex = findMessageIndex(
        state.ticketSelectedMessages,
        action.message,
      );

      if (!isFromSelectedTicket || messageIndex === -1) {
        return state;
      }

      if (
        isStaleMessageStatus(
          state.ticketSelectedMessages[messageIndex].idStatus,
          action.message.idStatus,
        )
      ) {
        return state;
      }

      return {
        ...state,
        ticketSelectedMessages: [
          ...state.ticketSelectedMessages.slice(0, messageIndex),
          action.message,
          ...state.ticketSelectedMessages.slice(messageIndex + 1),
        ],
      };
    }),
    on(TicketActions.newAgentMessage, (state, action) => {
      const ticket = state.tickets.find((x) => x.id == action.idTicket);

      if (!ticket) {
        return { ...state };
      }

      const isTicketSelected = ticket.id == state.ticketSelected?.id;

      const newTicketData: Ticket = {
        ...ticket,
        lastMessage: action.message,
      };

      return {
        ...state,
        tickets: [
          newTicketData,
          ...state.tickets.filter((x) => x.id != newTicketData.id),
        ],
        ticketSelected: isTicketSelected ? newTicketData : state.ticketSelected,
        ticketSelectedMessages: isTicketSelected
          ? [...state.ticketSelectedMessages, action.message]
          : state.ticketSelectedMessages,
      };
    }),
    on(TicketActions.removeAgentMessage, (state, action) => {
      const isTicketSelected = state.ticketSelected?.id === action.ticketId;
      const nextTickets = state.tickets.map((ticket) =>
        ticket.id === action.ticketId
          ? restoreFailedAgentMessage(
              ticket,
              action.tempId,
              action.previousLastMessage,
            )
          : ticket,
      );
      const nextSelectedTicket =
        state.ticketSelected && isTicketSelected
          ? restoreFailedAgentMessage(
              state.ticketSelected,
              action.tempId,
              action.previousLastMessage,
            )
          : state.ticketSelected;

      return {
        ...state,
        tickets: nextTickets,
        ticketSelected: nextSelectedTicket,
        ticketSelectedMessages: isTicketSelected
          ? state.ticketSelectedMessages.filter(
              (message) => message.tempId !== action.tempId,
            )
          : state.ticketSelectedMessages,
      };
    }),
    on(TicketActions.getMessageTemplatesForTicket, (state, action) => ({
      ...state,
      messageTemplates: [],
    })),
    on(TicketActions.getMessageTemplatesForTicketSuccess, (state, action) => ({
      ...state,
      messageTemplates: action.messageTemplates,
    })),
    on(TicketActions.selectTicketMessages, (state, action) => {
      const isFromSelectedTicket =
        state.ticketSelected && state.ticketSelected.idChat == action.chatId;

      return {
        ...state,
        ticketSelectedMessages: isFromSelectedTicket
          ? action.messages
          : state.ticketSelectedMessages,
      };
    }),
    on(TicketActions.loadChatUsersSuccess, (state, action) => ({
      ...state,
      chatUsers: action.users,
    })),
    on(TicketActions.getMessageTemplates, (state, action) => ({
      ...state,
      messageTemplates: [],
    })),
    on(TicketActions.getMessageTemplatesSuccess, (state, action) => ({
      ...state,
      messageTemplates: action.messageTemplates,
    })),
    on(TicketActions.createTicket, (state, action) => ({
      ...state,
      ticketCreated: false,
      ticketCreating: true,
      ticketCreateError: null,
    })),
    on(TicketActions.createTicketSuccess, (state, action) => ({
      ...state,
      ticketCreated: true,
      ticketCreating: false,
      ticketCreateError: null,
    })),
    on(TicketActions.createTicketFailure, (state, action) => ({
      ...state,
      ticketCreated: false,
      ticketCreating: false,
      ticketCreateError: action.message,
    })),
    on(TicketActions.clearTicketCreated, (state, action) => ({
      ...state,
      ticketCreated: false,
      ticketCreating: false,
      ticketCreateError: null,
    })),
    on(TicketActions.getHistoryTickets, (state, action) => ({
      ...state,
      historyTickets: action.append ? state.historyTickets : [],
      historyHasMore: false,
    })),
    on(TicketActions.getHistoryTicketsSuccess, (state, action) => ({
      ...state,
      historyTickets: action.append
        ? [...state.historyTickets, ...action.tickets]
        : action.tickets,
      historyHasMore: !!action.hasMore,
    })),
    on(TicketActions.clearSelectedTicket, (state, action) => ({
      ...state,
      ticketSelected: undefined,
      ticketSelectedMessages: [],
    })),
    on(logoutSuccess, (state, action) => ({
      ...state,
      tickets: [],
      historyTickets: [],
      historyHasMore: false,
      ticketSelected: undefined,
      ticketSelectedMessages: [],
      messageTemplates: [],
      chatUsers: [],
      ticketCreated: false,
    })),
    on(TicketActions.newReaction, (state, action) => {
      const ticket = state.tickets.find((x) => x.id == action.idTicket);

      if (!ticket) {
        return {
          ...state,
        };
      }

      const isTicketSelected = state.ticketSelected?.id == ticket.id;

      if (!isTicketSelected) {
        return {
          ...state,
        };
      }

      const messageIndex = state.ticketSelectedMessages.findIndex(
        (x) => x.id == action.reaction.messageId
      );

      if (messageIndex == -1) {
        return {
          ...state,
        };
      }

      const message = state.ticketSelectedMessages[messageIndex];

      return {
        ...state,
        ticketSelectedMessages: [
          ...state.ticketSelectedMessages.slice(0, messageIndex),
          {
            ...message,
            reactions: message.reactions
              ? [...message.reactions, action.reaction]
              : [action.reaction],
          },
          ...state.ticketSelectedMessages.slice(messageIndex + 1),
        ],
      };
    }),
    on(TicketActions.markTicketAsRead, (state, action) => {
      const ticketIndex = state.tickets.findIndex(
        (x) => x.id == action.ticketId
      );

      if (ticketIndex === -1) {
        return {
          ...state,
        };
      }

      const ticket = state.tickets[ticketIndex];
      const agentStatus = ticket.unreadStatuses.find(
        (x) => x.userId == action.agentId
      );

      let ticketData: Ticket;

      if (agentStatus) {
        ticketData = {
          ...ticket,
          unreadStatuses: [
            ...ticket.unreadStatuses.filter((x) => x.userId != action.agentId),
            {
              ...agentStatus,
              unreadCount: 0,
              lastReadMessageId: action.lastMessageId,
            },
          ],
        };
      } else {
        ticketData = {
          ...ticket,
          unreadStatuses: [
            ...ticket.unreadStatuses,
            {
              ticketId: action.ticketId,
              userId: action.agentId,
              unreadCount: 0,
              lastReadMessageId: action.lastMessageId,
            },
          ],
        };
      }

      return {
        ...state,
        tickets: [
          ...state.tickets.slice(0, ticketIndex),
          ticketData,
          ...state.tickets.slice(ticketIndex + 1),
        ],
      };
    })
  ),
});
